import React, { useState } from 'react';
import { FaEnvelope, FaRedoAlt, FaShieldAlt } from 'react-icons/fa';
import { http } from '../../lib/http';
import { isProfileEmailVerified, clearPendingEmailVerification, clearVerifyForBooking } from '../../utils/emailVerification';

export default function EmailVerificationBanner({ profile, lang, onVerified }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ err: '', ok: '' });

  const email = profile?.email || '';

  if (!profile || isProfileEmailVerified(profile) || !email) return null;

  const isAr = lang === 'ar';

  const resend = async () => {
    setBusy(true);
    setMsg({ err: '', ok: '' });
    try {
      const j = await http.post('/resend-verification-email', { email });
      if (j?.verificationEmailSent) {
        setMsg({
          ok: isAr
            ? 'تم إرسال رمز التحقق. تحقق من بريدك (ومجلد الرسائل غير المرغوب فيها).'
            : 'Verification code sent. Check your inbox and spam folder.',
          err: '',
        });
      } else {
        setMsg({
          err: j?.message || (isAr ? 'تعذر إرسال البريد. حاول مرة أخرى.' : 'Could not send email. Try again shortly.'),
          ok: '',
        });
      }
    } catch (e) {
      setMsg({ err: e.message || 'Failed to resend', ok: '' });
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (!/^\d{4}$/.test(code)) {
      setMsg({
        err: isAr ? 'أدخل رمزًا مكونًا من 4 أرقام' : 'Enter the 4-digit code',
        ok: '',
      });
      return;
    }
    setBusy(true);
    setMsg({ err: '', ok: '' });
    try {
      await http.post('/verify-email', { email, verificationCode: code });
      setMsg({
        ok: isAr ? 'تم التحقق! يمكنك الآن حجز موعد.' : 'Email verified! You can book now.',
        err: '',
      });
      setCode('');
      clearPendingEmailVerification();
      clearVerifyForBooking();
      onVerified?.();
    } catch (e) {
      setMsg({ err: e.message || 'Verification failed', ok: '' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="email-verify-banner-wrap" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="email-verify-banner" role="alert">
        <div className="email-verify-banner__accent" aria-hidden />

        <div className="email-verify-banner__top">
          <div className="email-verify-banner__icon-wrap" aria-hidden>
            <FaEnvelope className="email-verify-banner__icon" />
          </div>
          <div className="email-verify-banner__headline">
            <span className="email-verify-banner__badge">
              <FaShieldAlt aria-hidden />
              {isAr ? 'مطلوب' : 'Required'}
            </span>
            <h2 className="email-verify-banner__title">
              {isAr ? 'تحقق من بريدك الإلكتروني لإتمام الحجز' : 'Verify your email to book appointments'}
            </h2>
            <p className="email-verify-banner__lead">
              {isAr ? (
                <>
                  أدخل الرمز المكوّن من 4 أرقام المرسل إلى{' '}
                  <span className="email-verify-banner__email">{email}</span>
                  . تحقق من مجلد الرسائل غير المرغوب فيها إن لم تجده.
                </>
              ) : (
                <>
                  Enter the 4-digit code we sent to{' '}
                  <span className="email-verify-banner__email">{email}</span>
                  . Check your spam folder if you don&apos;t see it.
                </>
              )}
            </p>
          </div>
        </div>

        {(msg.err || msg.ok) && (
          <p
            className={msg.err ? 'email-verify-banner__feedback email-verify-banner__feedback--err' : 'email-verify-banner__feedback email-verify-banner__feedback--ok'}
            role="status"
          >
            {msg.err || msg.ok}
          </p>
        )}

        <form className="email-verify-banner__form" onSubmit={verify}>
          <label className="email-verify-banner__field">
            <span className="email-verify-banner__label">{isAr ? 'رمز التحقق' : 'Verification code'}</span>
            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              autoComplete="one-time-code"
              placeholder="• • • •"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
              className="email-verify-banner__code"
              aria-label={isAr ? 'رمز التحقق' : 'Verification code'}
            />
          </label>
          <button
            type="submit"
            className="email-verify-banner__btn email-verify-banner__btn--primary"
            disabled={busy || code.length !== 4}
          >
            {busy ? (isAr ? 'جاري التحقق…' : 'Verifying…') : isAr ? 'تحقق' : 'Verify'}
          </button>
        </form>

        <div className="email-verify-banner__footer">
          <button
            type="button"
            className="email-verify-banner__btn email-verify-banner__btn--ghost"
            onClick={resend}
            disabled={busy}
          >
            <FaRedoAlt aria-hidden />
            {isAr ? 'إعادة إرسال الرمز' : 'Resend code'}
          </button>
        </div>
      </div>
    </div>
  );
}
